import type { ReactNode } from 'react'

interface MetricCardProps {
  /** The main metric value to display prominently (e.g., "60%+", "100", "$1.2M") */
  value: string | number
  /** Label describing the metric (e.g., "Searches Processed by AI") */
  label: string
  /** Optional suffix to append to value (e.g., "%", "+", "k") */
  suffix?: string
  /** Optional prefix to prepend to value (e.g., "$", "#") */
  prefix?: string
  /** Optional icon to display above the metric */
  icon?: ReactNode
  /** Card size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Optional click handler */
  onClick?: () => void
  /** Optional className for custom styling */
  className?: string
  /** Color variant for the metric value */
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger'
}

const sizeStyles = {
  sm: {
    padding: 'p-4',
    value: 'text-2xl',
    label: 'text-xs',
    gap: 'gap-1',
  },
  md: {
    padding: 'p-6',
    value: 'text-3xl',
    label: 'text-sm',
    gap: 'gap-2',
  },
  lg: {
    padding: 'p-8',
    value: 'text-4xl',
    label: 'text-base',
    gap: 'gap-3',
  },
}

const variantStyles = {
  default: 'text-gray-900',
  primary: 'text-indigo-600',
  success: 'text-emerald-600',
  warning: 'text-amber-600',
  danger: 'text-red-600',
}

/**
 * MetricCard - A clean, prominent metric display component
 *
 * Design based on modern stat cards with large values and descriptive labels.
 * Perfect for dashboards, landing pages, and data summaries.
 *
 * @example
 * <MetricCard value="60" suffix="%+" label="Searches Processed by AI" />
 * <MetricCard value="100" suffix="%" label="First-Mover Advantage" variant="success" />
 * <MetricCard value={1250} prefix="$" suffix="k" label="Revenue Generated" size="lg" />
 */
export default function MetricCard({
  value,
  label,
  suffix = '',
  prefix = '',
  icon,
  size = 'md',
  onClick,
  className = '',
  variant = 'default',
}: MetricCardProps) {
  const styles = sizeStyles[size]
  const valueColor = variantStyles[variant]

  return (
    <div
      className={`
        bg-white rounded-xl border border-gray-200
        ${styles.padding}
        flex flex-col items-center justify-center text-center
        transition-all duration-200
        ${onClick ? 'cursor-pointer hover:border-gray-300 hover:shadow-md' : ''}
        ${className}
      `.trim()}
      onClick={onClick}
    >
      {icon && (
        <div className="mb-2 text-gray-500">
          {icon}
        </div>
      )}

      <div className={`${styles.value} font-bold ${valueColor} tracking-tight`}>
        {prefix}{value}{suffix}
      </div>

      <div className={`${styles.label} text-gray-600 mt-2 font-medium`}>
        {label}
      </div>
    </div>
  )
}

/**
 * MetricCardGroup - A wrapper for displaying multiple MetricCards in a row
 *
 * @example
 * <MetricCardGroup>
 *   <MetricCard value="60" suffix="%+" label="AI Searches" />
 *   <MetricCard value="50" suffix="%+" label="Non-Text Discovery" />
 *   <MetricCard value="100" suffix="%" label="First-Mover Advantage" />
 * </MetricCardGroup>
 */
export function MetricCardGroup({
  children,
  className = ''
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`}>
      {children}
    </div>
  )
}
